import {SharedColors} from "@fluentui/theme";
import Axios, {AxiosRequestConfig, CancelTokenSource} from "axios";
import {
    ActionButton,
    ComboBox,
    Dialog,
    DialogType,
    IButtonProps,
    IComboBox,
    IComboBoxOption,
    IComboBoxProps,
    IComboBoxStyles,
    IconButton,
    IRenderFunction,
    IStyle,
    Label,
    Spinner,
    Stack,
    Text,
} from "@fluentui/react";
import React, {CSSProperties, ReactElement, useCallback, useEffect, useMemo, useRef, useState,} from "react";
import {useTranslation} from "react-i18next";
import {MD5} from "object-hash";
import {theme} from "../index";
import "../styles/CustomSearchBox.css";
import {
    AdminUsersTenantReturnDto,
    ApplicationUser,
    Company,
    CompanyDto,
    CompanySettings,
    Contact,
    ContactEmail,
    Country,
    Currency,
    Department,
    DocumentType,
    Item,
    ItemDto,
    ItemGroup,
    ItemGroupItem,
    LabelComposition,
    LabelContinuousOrSingleSheetEnum,
    LabelDefinition,
    LabelDefinitionLocation,
    LabelDefinitionLocationEnum,
    OfficialVehicle, PagingModel,
    PersonalVehicle,
    Project,
    ProjectDto,
    QueryData,
    SavedFile,
    Subject,
    SupportTicketDto,
    THeSetItem,
    THeSetSubj,
    TPaReport,
    TPaSqli,
    TravelWarrantCostType,
    WorkRecordCostType,
} from "../API";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {StringHelpers} from "../helpers/StringHelpers";
import {TenantList} from "./TenantList";
import {TenantResource} from "../enums/TenantResource";
import {
    CheckboxElement,
    CustomSearchBoxElement,
    EnumDropdownElement,
    ITenantList,
    MultilineTextElement,
    NumberElement,
    TextFieldElement,
    TitleElement,
} from "./OnixElements";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {useErrorMessage} from "../hooks/useErrorMessage";
import {ExpenseBearer} from "../graphql-types";
import {ListItemCheckbox} from "./AsCheckMark";

export interface ICustomSearchBoxProps<P> {
    label?: JSX.Element | string;
    placeholder: string;
    selected?: P | string | null;
    searchUrl: string;
    listItemKey: keyof P;
    optionSelected?: (s?: P) => void;
    disabled?: boolean;
    errorMessage?: string | undefined;
    subtitleKey?: keyof P;
    subtitle?: string;
    makeSubtitle?: (a: P) => string;
    makeSubSubtitle?: (a: P) => string;
    makeTitle?: (p: P) => string;
    showCancelButton?: boolean;
    width?: number;
    rightSideIconButtonProps?: IButtonProps;
    leftSideIconButtonProps?: IButtonProps;
    titleKey?: keyof P;
    style?: CSSProperties;
    overrideBaseStyles?: CSSProperties;
    type: "DEFAULT_CRUD" | "TENANT_OLD";
    id?: string;
    searchBody?: Partial<QueryData>;
    hideBottomSubtitle?: boolean;
    multiple?: P[];
    nullElement?: Partial<P>;
    tabIndex?: number;
    title?: string;
    query?: ITenantResourceQuery<P>;
    additionalIntParameter?: number;
    additionalStringParameter?: string;
    errorStyle?: IStyle;
    showError?: boolean;
    subtitleIsImage?: boolean;
    dontShowValueInLabel?: boolean;
    customClassName?: string;
    fullWidth?: boolean;
    prefillSubtitleIfEmpty?: boolean;
    tenantResource?: TenantResource;
    headers?: { [key: string]: string };
    paging?: Partial<PagingModel>;
}

export const CustomSearchBox = <P extends any>({
                                                   fullWidth,
                                                   errorMessage,
                                                   customClassName,
                                                   subtitleIsImage,
                                                   dontShowValueInLabel,
                                                   showError,
                                                   errorStyle,
                                                   additionalIntParameter,
                                                   query,
                                                   nullElement,
                                                   multiple,
                                                   searchBody,
                                                   makeSubSubtitle,
                                                   tabIndex,
                                                   title,
                                                   rightSideIconButtonProps,
                                                   hideBottomSubtitle,
                                                   id,
                                                   overrideBaseStyles,
                                                   style,
                                                   titleKey,
                                                   subtitleKey,
                                                   leftSideIconButtonProps,
                                                   width,
                                                   showCancelButton,
                                                   disabled,
                                                   subtitle,
                                                   makeTitle,
                                                   makeSubtitle,
                                                   label,
                                                   placeholder,
                                                   selected,
                                                   searchUrl,
                                                   listItemKey,
                                                   optionSelected,
                                                   type,
                                                   prefillSubtitleIfEmpty,
                                                   additionalStringParameter,
                                                   tenantResource,
                                                   headers,
                                                   paging
                                               }: ICustomSearchBoxProps<P>) => {
    const combo = useRef<HTMLDivElement>(null);
    const [searchResults, setSearchResults] = useState<any[]>();
    const [loading, setLoading] = useState<boolean>(false);
    const [textEntry, setTextEntry] = useState("");
    const [loaded, setLoaded] = useState(false);
    const {t} = useTranslation();
    const [convertedSearchResults, setConvertedSearchResults] = useState<any[]>(
        [],
    );
    const comboBoxRef = useRef<IComboBox>(null);
    const cancelToken = useRef<CancelTokenSource>(Axios.CancelToken.source());
    const [refresh, setRefresh] = useState(false);
    const {localErrorMessage, clearLocalErrorMessage} =
        useErrorMessage(errorMessage);

    useEffect(() => {
        if (!selected) {
            setImmediate(() => setRefresh(true));
        }
    }, [selected]);

    useEffect(() => {
        if (refresh) {
            setImmediate(() => setRefresh(false));
        }
    }, [refresh]);

    const searchChanged = useCallback(
        (event: any, option: any, index: any, value: any) => {
            console.log("searchChanged", event, option, index, value);
            setTextEntry(event.target.value);
            if (option) {
                if (searchResults) {
                    const foundCustomer = searchResults.find(
                        (f) => f[listItemKey] == option.key,
                    );
                    console.log("foundCustomer", foundCustomer);
                    if (foundCustomer) optionSelected?.(foundCustomer);
                } else {
                    console.log("No search results");
                }
            } else {
                optionSelected?.(undefined);
            }
        },
        [listItemKey, optionSelected, searchResults],
    );

    const searchValue = useCallback(
        (value: string, force: boolean = false, focus: boolean = true) => {
            if (!multiple || force) {
                setLoading(true);
                const conf: AxiosRequestConfig = {
                    cancelToken: cancelToken.current.token,
                };
                if (type != "DEFAULT_CRUD" && searchBody) {
                    conf.data = searchBody;
                }

                const searchParameter =
                    searchUrl.indexOf("?") != -1 ? "&Search=" : "?Search=";

                if (type == "TENANT_OLD") {
                    Axios.get(searchUrl + searchParameter + value, conf)
                        .then(({data}) => {
                            setSearchResults([
                                ...(nullElement ? [nullElement] : []),
                                ...data,
                            ]);

                            if (data.length && focus) {
                                comboBoxRef.current?.focus(true);
                            }
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else if (type == "DEFAULT_CRUD") {
                    let data: any = searchBody ?? {};

                    if (!data) {
                        data = {};
                    }

                    if (!data?.paging && !paging) {
                        data.paging = {count: 10};
                    } else if (paging) {
                        data.paging = paging;
                    }

                    if (query) {
                        data = {...data, query};
                    }

                    if (additionalIntParameter) {
                        conf.params = {
                            ...conf.params,
                            additionalIntParameter: additionalIntParameter,
                        };
                    }

                    if (additionalStringParameter) {
                        conf.params = {
                            ...conf.params,
                            additionalStringParameter: additionalStringParameter,
                        };
                    }

                    if (headers) {
                        conf.headers = {
                            ...conf.headers,
                            ...headers,
                        };
                    }

                    Axios.post(searchUrl + searchParameter + value, data, conf)
                        .then(({data}) => {
                            setSearchResults([
                                ...(nullElement ? [nullElement] : []),
                                ...data.items,
                            ]);

                            if (data?.items?.length && focus) {
                                comboBoxRef.current?.focus(true);
                            }
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            } else if (multiple && searchResults?.length && focus) {
                comboBoxRef.current?.focus(true);
            }
        },
        [
            setSearchResults,
            searchResults,
            setLoading,
            loading,
            loaded,
            searchUrl,
            searchBody,
            nullElement,
            comboBoxRef,
            cancelToken,
            headers,
            query,
            additionalIntParameter,
            additionalStringParameter,
            multiple,
            paging
        ],
    );

    useEffect(() => {
        if (multiple && !searchResults) {
            searchValue("", true, false);
        }
    }, [multiple]);

    let searchCustomersInterval: any;
    const searchOptions = useCallback(
        ({target: {value}}: any, force: boolean = false) => {
            if (!value && !force) return;
            if (!loaded) return;
            if (searchCustomersInterval) clearTimeout(searchCustomersInterval);
            searchCustomersInterval = setTimeout(async () => {
                searchValue(value);
            }, 500);
        },
        [
            setSearchResults,
            setLoading,
            loading,
            loaded,
            searchUrl,
            searchBody,
            nullElement,
            comboBoxRef,
            cancelToken,
        ],
    );

    const resolveOptions = useCallback(
        (options: IComboBoxOption[]): Promise<IComboBoxOption[]> => {
            searchOptions({target: {value: ""}});
            return Promise.resolve(options);
        },
        [],
    );

    const resolveTagOptions = async () => {
        searchOptions({target: {value: ""}});
        return [];
    };

    const makeTitleFrom = useCallback(
        (f: P | string): string => {
            if (typeof f === "string") {
                return f ?? "";
            } else {
                if (makeTitle) {
                    let ret = "";
                    try {
                        ret = makeTitle(f);
                    } catch (exception) {
                    }
                    return ret ?? "";
                }

                if (titleKey && f) {
                    if ((f[titleKey] as any) instanceof Object) return "";
                    return (f[titleKey] as any) ?? "";
                }

                return "";
            }
        },
        [titleKey, makeTitle],
    );

    useEffect(() => {
        setConvertedSearchResults(
            searchResults?.map((a: any): IComboBoxOption => {
                let txt = "";
                let st = "";
                let subs = "";

                try {
                    txt = !!makeTitle
                        ? makeTitleFrom(a)
                        : !!titleKey
                            ? a[titleKey]
                            : a[listItemKey];
                } catch (exception) {
                }

                try {
                    st = !!makeSubtitle
                        ? makeSubtitle(a)
                        : !!subtitleKey
                            ? a[subtitleKey]
                            : "";
                } catch (exception) {
                }

                try {
                    subs = !!makeSubSubtitle ? makeSubSubtitle(a) : "";
                } catch (exception) {
                }

                const returning = {
                    key: (a ? a[listItemKey] : "") ?? "",
                    text: txt ?? "",
                    title: st ?? "",
                    data: subs ?? "",
                };

                return returning;
            }) ?? [],
        );
    }, [
        searchResults,
        makeSubtitle,
        makeSubSubtitle,
        makeTitle,
        setConvertedSearchResults,
        titleKey,
        subtitleKey,
    ]);

    useEffect(() => {
        setTimeout(() => setLoaded(true));
    }, []);

    const renderOption = useCallback(
        (item: any): any => (
            <Stack tokens={{childrenGap: 0}} gap={0}>
                <Text>{item?.text ?? ""}</Text>
                {(!!makeSubtitle || !!subtitleKey) && !subtitleIsImage ? (
                    <Text
                        variant={"tiny"}
                        styles={{root: {color: theme.palette?.themePrimary}}}
                    >
                        {item?.title ?? ""}
                    </Text>
                ) : null}
                {!!makeSubSubtitle ? (
                    <Text
                        variant={"tiny"}
                        styles={{root: {color: SharedColors.red10}}}
                    >
                        <b>{item?.data ?? ""}</b>
                    </Text>
                ) : null}
            </Stack>
        ),
        [makeSubtitle, makeSubSubtitle, subtitleKey, subtitleIsImage],
    );

    const makeText = useMemo((): string => {
        if (multiple != undefined) {
            return multiple
                .map((m) => {
                    if (makeTitle) {
                        let ret = "";
                        try {
                            ret = makeTitle(m) ?? "";
                        } catch (exception) {
                        }
                        return ret ?? "";
                    } else if (titleKey) {
                        if ((m[titleKey] as any) instanceof Object) return "";
                        return m[titleKey] ?? "";
                    } else {
                        return (m as any).toString() ?? "";
                    }
                })
                .join(", ");
        } else if (selected) {
            if (makeTitle) {
                let ret = "";
                try {
                    ret = makeTitleFrom(selected) ?? "";
                } catch (exception) {
                }
                return ret ?? "";
            } else if (titleKey) {
                if ((selected as any)[titleKey]) {
                    return (((selected as any)[titleKey as any] + "") as any) || "";
                } else {
                    if (selected instanceof Object) return "";
                    return (selected as string) ?? "";
                }
            } else {
                if ((selected as any)[listItemKey]) {
                    return (((selected as any)[listItemKey as any] + "") as any) || "";
                } else {
                    if (selected instanceof Object) return "";
                    return (selected as string) ?? "";
                }
            }
        } else {
            return textEntry ?? "";
        }
    }, [selected, makeTitle, titleKey, listItemKey, textEntry, multiple]);

    const comboboxStyles = useMemo<Partial<IComboBoxStyles>>(
        () => ({
            input: {width, flex: 1},
            root: {flex: 1},
            optionsContainer: {flex: 1},
            container: {flex: 1, minWidth: width ?? 100},
            errorMessage: errorStyle,
            callout: {minWidth: 300},
        }),
        [],
    );

    const focused = useCallback(() => {
        cancelToken.current = Axios.CancelToken.source();
        clearLocalErrorMessage();
    }, [cancelToken, clearLocalErrorMessage]);

    const unfocused = useCallback(() => {
        cancelToken?.current.cancel("Unfocused");
    }, [cancelToken]);

    const renderBottomSubtitle = useCallback(() => {
        if (hideBottomSubtitle) return null;

        let subTitleText = "";

        if (
            subtitleKey &&
            typeof selected !== "string" &&
            selected &&
            selected[subtitleKey]
        ) {
            let tempSub: any = selected[subtitleKey];
            if (typeof tempSub !== "string") {
                tempSub = "";
            }
            subTitleText = tempSub as string;
        }

        if (subtitleIsImage && subTitleText) {
            const base64Image = `data:image/png;base64,${subTitleText}`;
            return (
                <img
                    src={base64Image}
                    style={{border: "1px solid red", maxHeight: 150}}
                />
            );
        } else {
            if (!subTitleText) return null;

            return (
                <Text
                    variant={"small"}
                    style={{
                        whiteSpace: "pre",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                    }}
                >
                    {subTitleText}
                </Text>
            );
        }
    }, [selected, subtitleKey, makeSubtitle, subtitleIsImage]);

    const selectedKeys = useMemo(() => {
        let ret: any = undefined;
        if (!!multiple) {
            ret = multiple.map((m) => m[listItemKey]);
        } else if (!!selected) {
            if (
                selected instanceof Object ||
                selected instanceof Array ||
                typeof selected === "object"
            ) {
                ret = (selected as any)[listItemKey];
            } else {
                console.error(
                    "Selected is not an object or array",
                    selected,
                    "looking for key",
                    listItemKey,
                    "in",
                    selected,
                    "which is a",
                    typeof selected,
                    "and is",
                    selected instanceof Object ? "an object" : "not an object",
                );
                ret = selected;
            }
        }
        // console.log("Selected keys", ret, multiple, selected, listItemKey);
        if (multiple && multiple.length === 0 && !searchResults?.length)
            return undefined;
        return ret;
    }, [multiple, selected, listItemKey, searchResults]);

    const [showingDetailedSearch, setShowingDetailedSearch] = useState<boolean>();

    const showDetailedSearch = useCallback(() => {
        comboBoxRef.current?.dismissMenu();
        setShowingDetailedSearch(true);
    }, [comboBoxRef.current]);

    const selectFromDetails = useCallback(
        (item: any) => {
            console.log("Selecting from details", item);
            optionSelected?.(item);
            comboBoxRef.current?.dismissMenu();
            setShowingDetailedSearch(false);
        },
        [comboBoxRef.current, optionSelected, setShowingDetailedSearch],
    );

    const tenantDetailedSearchElement = useMemo<
        (() => ReactElement) | undefined
    >(() => {
        // const foundTenantResourceKey = Object.keys(TenantResource).find((f) => {
        //   return (searchUrl.startsWith("/") ? searchUrl.slice(1) : searchUrl)
        //     .toLowerCase()
        //     .startsWith((TenantResource as any)[f].toLowerCase());
        // }) as keyof TenantResource;

        if (tenantResource) {
            const baseTenantListProps: ITenantList<any, any, any> = {
                resource: tenantResource,
                baseDto: {},
                columns: [],
                allowSearching: true,
                allowedActions: [],
                title: "detailed-search",
                query: query,
                id: `${tenantResource?.toString()}-${tenantResource}-${MD5(
                    JSON.stringify(query ?? {}),
                )}`,
                additionalIntParameter: additionalIntParameter,
                additionalStringParameter: additionalStringParameter,
                headers: headers,
            };

            switch (tenantResource) {
                case TenantResource.WebDocumentTHeSetSubj:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<THeSetSubj>(
                                {
                                    acSubject: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.acSubject}
                      </span>
                                        ),
                                    },
                                    acName2: {},
                                    acPost: {},
                                    acPhone: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.WebDocumentTHeSetItem:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<THeSetItem>(
                                {
                                    acIdent: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.acIdent}
                      </span>
                                        ),
                                    },
                                    acName: {},
                                    anSalePrice: {
                                        render: (item) => (
                                            <span>
                        {new Intl.NumberFormat("hr-HR", {
                            currency: item.acCurrency ?? "EUR",
                            minimumFractionDigits: 2,
                        }).format(item.anSalePrice)}
                      </span>
                                        ),
                                    },
                                    anRtprice: {
                                        render: (item) => (
                                            <span>
                        {new Intl.NumberFormat("hr-HR", {
                            currency: item.acCurrency ?? "EUR",
                            minimumFractionDigits: 2,
                        }).format(item.anRtprice)}
                      </span>
                                        ),
                                    },
                                    anWsprice2: {
                                        render: (item) => (
                                            <span>
                        {new Intl.NumberFormat("hr-HR", {
                            currency: item.acCurrency ?? "EUR",
                            minimumFractionDigits: 2,
                        }).format(item.anWsprice2)}
                      </span>
                                        ),
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.AdminDocumentTypes:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<DocumentType>(
                                {
                                    id: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.id}
                      </span>
                                        ),
                                        maxWidth: 30,
                                    },
                                    acName: {
                                        minWidth: 300,
                                    },
                                    userId: {
                                        minWidth: 300,
                                    },
                                    printDocumentTitleOverride: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Contact:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            allowedActions={["PUT", "POST", "DELETE"]}
                            itemInvoked={selectFromDetails}
                            addingTitle="adding-new-contact"
                            editingTitle="editing-contact"
                            form={{
                                fullName: {
                                    type: new TextFieldElement({}),
                                },
                                note: {
                                    type: new MultilineTextElement({
                                        rows: 10,
                                        showEditModeToggle: true,
                                    }),
                                },
                                deactivated: {
                                    type: new CheckboxElement({}),
                                },
                            }}
                            columns={MakeTenantColumns<Contact>(
                                {
                                    id: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.id}
                      </span>
                                        ),
                                    },
                                    fullName: {},
                                    emails: {
                                        render: (item) => (
                                            <span>{item.emails?.map((e) => e.email).join(", ")}</span>
                                        ),
                                    },
                                    note: {},
                                    phoneNumbers: {
                                        render: (item) => (
                                            <span>
                        {item.phoneNumbers
                            ?.map((e) => e.phoneNumber)
                            .join(", ")}
                      </span>
                                        ),
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Company:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            allowedActions={["PUT", "POST", "DELETE"]}
                            form={{
                                name: {
                                    type: new TextFieldElement({}),
                                },
                                pantheonId: {
                                    type: new CustomSearchBoxElement<THeSetSubj, CompanyDto>({
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        url: "/PantheonTHeSetSubj/List",
                                        key: "acSubject",
                                        titleKey: "acName2",
                                        subtitleKey: "acSubject",
                                        resource: TenantResource.PantheonTHeSetSubj,
                                    }),
                                },
                                // pantheonStatus: {
                                //     type: new TitleElement<THeSetSubj>({}),
                                // },
                                // pantheonBuyerType: {
                                //     type: new TitleElement<THeSetSubj>({}),
                                // },
                                description: {
                                    type: new MultilineTextElement({
                                        rows: 7,
                                        showEditModeToggle: true,
                                    }),
                                },
                                notes: {
                                    type: new MultilineTextElement({
                                        rows: 12,
                                        showEditModeToggle: true,
                                    }),
                                },
                                connectionSettings: {
                                    type: new MultilineTextElement({
                                        rows: 12,
                                        showEditModeToggle: true,
                                    }),
                                },
                                myEInvoiceSettings: {
                                    type: new MultilineTextElement({
                                        rows: 5,
                                        showEditModeToggle: true,
                                    }),
                                },
                            }}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<Company>(
                                {
                                    id: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.id}
                      </span>
                                        ),
                                    },
                                    name: {},
                                    description: {},
                                    notes: {},
                                    pantheonStatus: {},
                                    pantheonId: {},
                                },

                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.SupportUsers:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<ApplicationUser>(
                                {
                                    id: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.id}
                      </span>
                                        ),
                                    },
                                    fullName: {},
                                    userName: {},
                                    email: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Projects:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            allowedActions={["POST", "PUT", "DELETE"]}
                            form={{
                                name: {
                                    type: new TextFieldElement({}),
                                },
                                description: {
                                    type: new MultilineTextElement({rows: 10}),
                                },
                                subjectId: {
                                    type: new CustomSearchBoxElement<Subject, ProjectDto>({
                                        url: "Subjects/List",
                                        key: "id",
                                        titleKey: "shortName",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        subtitleKey: "longName",
                                        resource: TenantResource.Subjects,
                                    }),
                                },
                                finished: {
                                    type: new CheckboxElement({}),
                                },
                            }}
                            columns={MakeTenantColumns<Project>(
                                {
                                    id: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.id}
                      </span>
                                        ),
                                    },
                                    name: {},
                                    description: {},
                                    dueDate: {},
                                    finished: {
                                        render(item) {
                                            return <ListItemCheckbox checked={item.finished}/>;
                                        },
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.SupportTicketCRUD:
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            form={{
                                title: {
                                    type: new TextFieldElement({}),
                                },
                                companyId: {
                                    type: new CustomSearchBoxElement<Company, SupportTicketDto>({
                                        url: "/Companies/List",
                                        key: "id",
                                        titleKey: "name",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        subtitleKey: "description",
                                        resource: TenantResource.Company,
                                    }),
                                },
                                contactId: {
                                    type: new CustomSearchBoxElement<Contact, SupportTicketDto>({
                                        url: "/Contacts/List",
                                        key: "id",
                                        titleKey: "fullName",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        subtitleKey: "note",
                                        resource: TenantResource.Contact,
                                    }),
                                },
                                userId: {
                                    type: new CustomSearchBoxElement<
                                        ApplicationUser,
                                        SupportTicketDto
                                    >({
                                        url: "/SupportUsers/List",
                                        key: "id",
                                        titleKey: "fullName",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        subtitleKey: "userName",
                                        resource: TenantResource.SupportUsers,
                                    }),
                                },
                                temporaryOwnerId: {
                                    type: new CustomSearchBoxElement<
                                        ApplicationUser,
                                        SupportTicketDto
                                    >({
                                        url: "/SupportUsers/List",
                                        key: "id",
                                        titleKey: "fullName",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        subtitleKey: "userName",
                                        resource: TenantResource.SupportUsers,
                                    }),
                                },
                            }}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<SupportTicketDto>(
                                {
                                    id: {
                                        render: (item) => (
                                            <span
                                                className={"has-clickable-action"}
                                                onClick={() => selectFromDetails(item)}
                                            >
                        {item.id}
                      </span>
                                        ),
                                        maxWidth: 50,
                                    },
                                    title: {
                                        maxWidth: 50,
                                    },
                                    source: {
                                        maxWidth: 50,
                                    },
                                    status: {
                                        maxWidth: 50,
                                    },
                                    company: {},
                                    contact: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.PantheonTPaReport: //TPaReport
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<TPaReport>(
                                {
                                    acReport: {},
                                    acName: {},
                                    acActive: {
                                        render(item) {
                                            return (
                                                <ListItemCheckbox checked={item.acActive === "T"}/>
                                            );
                                        },
                                    },
                                    acNote: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.TravelWarrantCostType: //TravelWarrantCostType
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<TravelWarrantCostType>(
                                {
                                    type: {},
                                    name: {},
                                    docType: {},
                                    pantheonId: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Currency: //Currency
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<Currency>(
                                {
                                    code: {},
                                    name: {},
                                    longName: {
                                        minWidth: 300,
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Country: //Country
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<Country>(
                                {
                                    code: {},
                                    name: {},
                                    currency: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.ItemGroup: //ItemGroup
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            allowedActions={["POST", "PUT", "DELETE"]}
                            form={{
                                name: {
                                    type: new TextFieldElement({}),
                                },
                            }}
                            columns={MakeTenantColumns<ItemGroup>(
                                {
                                    name: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Department: //Department
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            allowedActions={["POST", "PUT", "DELETE"]}
                            form={{
                                name: {
                                    type: new TextFieldElement({}),
                                },
                            }}
                            columns={MakeTenantColumns<Department>(
                                {
                                    name: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Subjects: //Subject
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<Subject>(
                                {
                                    shortName: {},
                                    longName: {},
                                    contactName: {},
                                    pantheonId: {},
                                    mail: {},
                                    address: {},
                                    VATID: {},
                                    VATIDPrefix: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.PantheonTHeSetSubj: //THeSetSubj
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<THeSetSubj>(
                                {
                                    acSubject: {},
                                    acName2: {
                                        minWidth: 300,
                                    },
                                    acActive: {
                                        render(item) {
                                            return (
                                                <ListItemCheckbox checked={item.acActive === "T"}/>
                                            );
                                        },
                                    },
                                    acNote: {},
                                    acPin: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.ItemGroupItems: //ItemReturnDto
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<ItemGroupItem>(
                                {
                                    itemGroup: {
                                        render(item) {
                                            return (
                                                <span>
                          {item.itemGroup?.name ?? item.itemGroup?.id ?? " - "}
                        </span>
                                            );
                                        },
                                    },
                                    item: {
                                        render(item) {
                                            return (
                                                <span>{item.item?.name ?? item.item?.id ?? " - "}</span>
                                            );
                                        },
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.TenantUsers: //ApplicationUser
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<ApplicationUser>(
                                {
                                    userName: {},
                                    fullName: {},
                                    email: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.Item: //Item
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            allowedActions={["POST", "PUT", "DELETE"]}
                            itemInvoked={selectFromDetails}
                            form={{
                                code: {
                                    type: new TextFieldElement({}),
                                },
                                name: {
                                    type: new TextFieldElement({}),
                                },
                                itemGroupId: {
                                    type: new CustomSearchBoxElement<ItemGroup, ItemDto>({
                                        url: "ItemGroup/List",
                                        key: "id",
                                        titleKey: "name",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        inlineFormResource: TenantResource.ItemGroup,
                                        resource: TenantResource.ItemGroup,
                                    }),
                                },
                            }}
                            columns={MakeTenantColumns<Item>(
                                {
                                    name: {},
                                    pantheonId: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.MobileReleaseSavedFiles: //SavedFile
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<SavedFile>(
                                {
                                    name: {},
                                    path: {},
                                    serverPath: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.ExpenseBearer: // ExpenseBearer
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<ExpenseBearer>(
                                {
                                    bearer: {},
                                    name: {},
                                    active: {
                                        render(item) {
                                            return <ListItemCheckbox checked={item.active}/>;
                                        },
                                    },
                                    country: {},
                                    currency: {},
                                    place: {},
                                    receiver: {},
                                    group: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.ActiveCostTypes: // WorkRecordCostType
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<WorkRecordCostType>(
                                {
                                    costType: {},
                                    active: {
                                        render(item) {
                                            return <ListItemCheckbox checked={item.active}/>;
                                        },
                                    },
                                    pantheonCostGroup: {},
                                    pantheonCostGroupName: {},
                                    pantheonCostType: {},
                                    pantheonName: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.ContactEmail: // ContactEmail
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            allowedActions={["PUT", "POST", "DELETE"]}
                            form={{
                                email: {
                                    type: new TextFieldElement({}),
                                },
                                isPrimary: {
                                    type: new CheckboxElement({}),
                                },
                            }}
                            columns={MakeTenantColumns<ContactEmail>(
                                {
                                    contact: {
                                        render(item) {
                                            return (
                                                <span>
                          {item.contact?.fullName ?? item.contact?.id ?? " - "}
                        </span>
                                            );
                                        },
                                    },
                                    email: {},
                                    isPrimary: {
                                        render(item) {
                                            return <ListItemCheckbox checked={item.isPrimary}/>;
                                        },
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.WorkRecordsUsers: // AdminUsersTenantReturnDto
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<AdminUsersTenantReturnDto>(
                                {
                                    firstName: {},
                                    lastName: {},
                                    userName: {},
                                    email: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.AresTPaSqli: // TPaSqli
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<TPaSqli>(
                                {
                                    acKey: {},
                                    acName: {},
                                    acActive: {
                                        render(item) {
                                            return (
                                                <ListItemCheckbox checked={item.acActive === "T"}/>
                                            );
                                        },
                                    },
                                    acNote: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.OfficialVehicle: // OfficialVehicle
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<OfficialVehicle>(
                                {
                                    makeAndModel: {},
                                    registration: {},
                                    pantheonId: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.PersonalVehicle: // PersonalVehicle
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<PersonalVehicle>(
                                {
                                    makeAndModel: {},
                                    registration: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.WorkRecordsOverviewUsers: // ApplicationUser
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<ApplicationUser>(
                                {
                                    firstName: {},
                                    lastName: {},
                                    userName: {},
                                    email: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.AllExpenseBearers: // ExpenseBearer
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<ExpenseBearer>(
                                {
                                    bearer: {},
                                    name: {},
                                    active: {
                                        render(item) {
                                            return <ListItemCheckbox checked={item.active}/>;
                                        },
                                    },
                                    country: {},
                                    currency: {},
                                    place: {},
                                    receiver: {},
                                    group: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.AdminCompaniesTenant: //CompanySettings
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            allowedActions={["PUT", "POST", "DELETE"]}
                            form={{
                                connectionName: {
                                    type: new TextFieldElement({}),
                                },
                                pantheonCompanyName: {
                                    type: new TextFieldElement({}),
                                },
                                pantheonAnDocKeyLenNumber: {
                                    type: new NumberElement({}),
                                },
                                pantheonAcCompanyPrtTextA: {
                                    type: new MultilineTextElement({}),
                                },
                                pantheonAcCompanyPrtTextB: {
                                    type: new MultilineTextElement({
                                        rows: 7,
                                    }),
                                },
                                support: {
                                    type: new TitleElement({}),
                                },
                                supportEmailAddress: {
                                    type: new TextFieldElement({}),
                                },
                                supportEmailPassword: {
                                    type: new TextFieldElement({}),
                                },
                                email: {
                                    type: new TitleElement({}),
                                },
                                companyDocumentsEmail: {
                                    type: new TextFieldElement({}),
                                },
                            }}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<CompanySettings>(
                                {
                                    connectionName: {minWidth: 500},
                                    pantheonCompanyName: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.AdminUsersTenant: //AdminUsersTenantDto
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<AdminUsersTenantReturnDto>(
                                {
                                    firstName: {},
                                    lastName: {},
                                    userName: {},
                                    email: {},
                                    companySettings: {
                                        render(item) {
                                            return (
                                                <span>
                          {item.companySettings?.pantheonCompanyName ??
                              item.companySettings?.connectionName ??
                              " - "}
                        </span>
                                            );
                                        },
                                    },
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.LabelDefinitions: //AdminUsersTenantDto
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            allowedActions={["PUT", "POST", "DELETE"]}
                            form={{
                                manufacturer: {
                                    type: new TextFieldElement({}),
                                },
                                manufacturerId: {
                                    type: new TextFieldElement({}),
                                },
                                name: {
                                    type: new TextFieldElement({}),
                                },
                                continuousOrSingleSheet: {
                                    type: new EnumDropdownElement({
                                        enumData: LabelContinuousOrSingleSheetEnum,
                                        showCancel: true,
                                    }),
                                    enum: "ContinuousOrSingleSheet",
                                },
                                lHDist: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lHDistance",
                                },
                                lVDist: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lVDistance",
                                },
                                lWidth: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lWidth",
                                },
                                lHeight: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lHeight",
                                },
                                lLeft: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lLeft",
                                },
                                lUpper: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lUpper",
                                },
                                nCols: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-nCols",
                                },
                                nRows: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-nRows",
                                },
                                lPWidth: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lPWidth",
                                },
                                lPHeight: {
                                    type: new NumberElement({}),
                                    translationKey: "Label-lPHeight",
                                },
                                companySettingsId: {
                                    type: new CustomSearchBoxElement<
                                        CompanySettings,
                                        LabelDefinition
                                    >({
                                        resource: TenantResource.AdminSingleCompanyTenant,
                                        titleKey: "connectionName",
                                        key: "id",
                                        customSearchBoxType: "DEFAULT_CRUD",
                                    }),
                                },
                            }}
                            itemInvoked={selectFromDetails}
                            columns={MakeTenantColumns<LabelDefinition>(
                                {
                                    manufacturer: {},
                                    manufacturerId: {},
                                    name: {
                                        minWidth: 300,
                                    },
                                    continuousOrSingleSheet: {},
                                    lHDist: {},
                                    lVDist: {},
                                    lWidth: {},
                                    lHeight: {},
                                    lLeft: {},
                                    lUpper: {},
                                    nCols: {},
                                    nRows: {},
                                    lPWidth: {},
                                    lPHeight: {},
                                },
                                t,
                                150,
                            )}
                        />
                    );
                case TenantResource.LabelDefinitionLocations: //AdminUsersTenantDto
                    return () => (
                        <TenantList<any, any, any>
                            {...baseTenantListProps}
                            allowedActions={["PUT", "POST", "DELETE"]}
                            form={{
                                location: {
                                    type: new EnumDropdownElement({
                                        enumData: LabelDefinitionLocationEnum,
                                    }),
                                },
                                labelDefinitionId: {
                                    type: new CustomSearchBoxElement<
                                        LabelDefinition,
                                        LabelDefinitionLocation
                                    >({
                                        key: "id",
                                        titleKey: "name",
                                        subtitleKey: "manufacturer",
                                        resource: TenantResource.LabelDefinitions,
                                        customSearchBoxType: "DEFAULT_CRUD",
                                    }),
                                },
                                companySettingsId: {
                                    type: new CustomSearchBoxElement<
                                        CompanySettings,
                                        LabelDefinitionLocation
                                    >({
                                        key: "id",
                                        titleKey: "connectionName",
                                        resource: TenantResource.AdminSingleCompanyTenant,
                                        customSearchBoxType: "DEFAULT_CRUD",
                                    }),
                                },
                                documentTypeId: {
                                    type: new CustomSearchBoxElement<
                                        DocumentType,
                                        LabelDefinitionLocation
                                    >({
                                        key: "id",
                                        titleKey: "acName",
                                        resource: TenantResource.AdminDocumentTypes,
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        queryFromDto(dto) {
                                            if (dto) {
                                                return {
                                                    companySettingsId: {
                                                        Equals: dto.companySettingsId,
                                                    },
                                                };
                                            }
                                        },
                                    }),
                                    disabledIf(dto: any) {
                                        return !dto?.companySettingsId;
                                    },
                                },
                                labelCompositionId: {
                                    type: new CustomSearchBoxElement<
                                        LabelComposition,
                                        LabelDefinitionLocation
                                    >({
                                        key: "id",
                                        titleKey: "name",
                                        subtitleKey: "id",
                                        resource: TenantResource.LabelComposition,
                                        customSearchBoxType: "DEFAULT_CRUD",
                                        queryFromDto(dto) {
                                            if (dto) {
                                                return {
                                                    companySettingsId: {
                                                        Equals: dto.companySettingsId,
                                                    },
                                                };
                                            }
                                        },
                                    }),
                                    disabledIf(dto: any) {
                                        return !dto?.companySettingsId;
                                    },
                                },
                            }}
                            columns={MakeTenantColumns<LabelDefinitionLocation>(
                                {
                                    labelDefinitionId: {
                                        translation: "id",
                                        maxWidth: 50,
                                    },
                                    labelDefinition: {
                                        render(item) {
                                            return <span>{item.labelDefinition?.name ?? " - "}</span>;
                                        },
                                        minWidth: 300,
                                    },
                                    documentTypeId: {
                                        translation: "id",
                                        maxWidth: 50,
                                    },
                                    documentType: {
                                        render(item) {
                                            return <span>{item.documentType?.acName ?? " - "}</span>;
                                        },
                                        minWidth: 300,
                                    },
                                    location: {
                                        render(item) {
                                            return (
                                                <span>
                          {t(LabelDefinitionLocationEnum[item.location])}
                        </span>
                                            );
                                        },
                                    },
                                },
                                t,
                            )}
                        />
                    );
                default:
                    console.log(
                        "No tenant resource found for url",
                        searchUrl,
                        "and resource",
                        tenantResource,
                    );
                    break;
            }
        }
    }, [
        tenantResource,
        query,
        additionalIntParameter,
        additionalStringParameter,
        searchUrl,
        selectFromDetails,
        t,
        headers,
    ]);

    const onRenderUpperContent = useCallback<IRenderFunction<IComboBoxProps>>(
        (
            props?: IComboBoxProps,
            defaultRender?: (props?: IComboBoxProps) => JSX.Element | null,
        ): JSX.Element | null => {
            if (!tenantDetailedSearchElement)
                return defaultRender?.call(props) ?? <div/>;
            return (
                <Stack
                    styles={{root: {backgroundColor: theme.palette.themePrimary}}}
                    horizontal
                    horizontalAlign="start"
                >
                    <ActionButton
                        styles={{
                            icon: {color: "white"},
                            root: {color: "white"},
                            rootHovered: {color: "white"},
                            iconHovered: {color: "white"},
                            iconPressed: {color: theme.palette.themeLighter},
                            rootPressed: {color: theme.palette.themeLighter},
                        }}
                        text="Detaljno pretraživanje"
                        iconProps={{iconName: "Search"}}
                        onClick={showDetailedSearch}
                    />
                </Stack>
            );
        },
        [showDetailedSearch, tenantDetailedSearchElement],
    );

    if (refresh) return null;

    return (
        <Stack
            style={
                overrideBaseStyles
                    ? overrideBaseStyles
                    : {
                        flex: 1,
                        minWidth: width ?? 100,
                        maxWidth: fullWidth ? undefined : (width ?? 300),
                    }
            }
            key={id}
            id={id}
        >
            {!!label ? (
                <Stack horizontal tokens={{childrenGap: 10}} verticalAlign={"start"}>
                    <Label>{(label ?? "") === "string" ? (label ?? "") : label}</Label>
                    {/* {loading && <Spinner />} */}
                </Stack>
            ) : null}
            <Stack horizontal tokens={{childrenGap: 10}} style={{flex: 1}}>
                {!!leftSideIconButtonProps ? (
                    <IconButton {...leftSideIconButtonProps} />
                ) : null}
                <Stack horizontal style={{flex: 1}}>
                    <ComboBox
                        className={customClassName}
                        iconButtonProps={{
                            onClick: () => {
                                comboBoxRef.current?.focus();
                                searchValue("", false, true);
                            },
                            onRenderIcon: loading ? () => <Spinner/> : undefined,
                            // styles:{root: {backgroundColor: "rgb(243, 242, 241)"}}
                        }}
                        calloutProps={{
                            onDismiss: () => {
                            },
                            preventDismissOnScroll: true,
                        }}
                        required
                        placeholder={placeholder}
                        onDismiss={() => {
                        }}
                        onMenuDismiss={() => {
                        }}
                        onMenuDismissed={() => {
                        }}
                        text={makeText}
                        multiSelect={multiple != undefined}
                        onChange={(ev, option, index, value) =>
                            searchChanged(ev, option, index, value)
                        }
                        ref={combo}
                        onBlur={unfocused}
                        tabIndex={tabIndex}
                        // useComboBoxAsMenuWidth
                        unselectable="on"
                        title={title}
                        onFocus={focused}
                        onRenderOption={renderOption}
                        onRenderUpperContent={onRenderUpperContent}
                        selectedKey={selectedKeys}
                        onResolveOptions={resolveOptions}
                        errorMessage={StringHelpers.ConditionalCombine(
                            {
                                if: () => searchResults?.length == 0,
                                show: t("no_results"),
                            },
                            {
                                if: () => !!localErrorMessage,
                                show: localErrorMessage ? t(localErrorMessage) : "",
                            },
                        )}
                        disabled={disabled != undefined ? disabled : false}
                        componentRef={comboBoxRef}
                        allowFreeform={!multiple}
                        autoComplete={"off"}
                        autoCapitalize={"none"}
                        autoCorrect={"none"}
                        spellCheck={false}
                        onInput={searchOptions}
                        styles={comboboxStyles}
                        style={style}
                        options={convertedSearchResults}
                    />
                    {!!tenantDetailedSearchElement ? (
                        <IconButton
                            onClick={(e) => setShowingDetailedSearch(true)}
                            iconProps={{iconName: "Search"}}
                            disabled={disabled}
                        />
                    ) : null}
                    {!!showCancelButton ? (
                        <IconButton
                            disabled={disabled}
                            title={t("clear")}
                            ariaLabel={t("clear")}
                            styles={{icon: {color: SharedColors.red10}}}
                            iconProps={{iconName: "Cancel"}}
                            onClick={() => {
                                setTextEntry("");
                                optionSelected?.(undefined);
                            }}
                        />
                    ) : null}
                </Stack>
                {!!rightSideIconButtonProps ? (
                    <IconButton {...rightSideIconButtonProps} />
                ) : null}
            </Stack>

            {subtitleIsImage ? (
                <div
                    style={{
                        whiteSpace: "pre",
                        backgroundColor: "white",
                    }}
                >
                    {renderBottomSubtitle() ??
                        (!!prefillSubtitleIfEmpty ? <div style={{height: 16}}/> : null)}
                </div>
            ) : (
                <div
                    style={{
                        minWidth: 100,
                        maxWidth: 200,
                        textOverflow: "ellipsis",
                        whiteSpace: "pre",
                        overflow: "hidden",
                        backgroundColor: "white",
                    }}
                >
                    {renderBottomSubtitle() ??
                        (!!prefillSubtitleIfEmpty ? <div style={{height: 16}}/> : null)}
                </div>
            )}

            {showingDetailedSearch ? (
                <Dialog
                    hidden={false}
                    onDismiss={() => setShowingDetailedSearch(false)}
                    minWidth={"75vw"}
                    dialogContentProps={{
                        type: DialogType.normal,
                        styles: {
                            inner: {
                                padding: 0,
                            },
                            header: {
                                padding: 0,
                                height: 0,
                            },
                            title: {
                                height: 0,
                            },
                        },
                    }}
                >
                    {tenantDetailedSearchElement?.call(this)}
                </Dialog>
            ) : null}
        </Stack>
    );
};
